/**
 * Converts straight ASCII quotes to proper directional (typographic) quotes.
 *
 * Space Grotesk (the app font) renders U+0027 as a right-leaning glyph,
 * making opening quotes look like closing quotes. This function:
 *   1. Flattens all Unicode quote variants to straight ASCII equivalents.
 *   2. Re-applies proper directional quotes using context-aware heuristics.
 *
 * Safe to call on any string — no-ops on strings without quote characters.
 */
export function normalizeQuotes(text: string): string {
  if (!text) return text;

  let s = text;

  // Step 1 — flatten every quote-like codepoint to a straight ASCII equivalent.
  s = s.replace(/[\u2018\u2019\u02BC\u02BB]/g, "'");   // single-quote family → '
  s = s.replace(/[\u201C\u201D]/g, '"');                 // double-quote family → "

  // Step 2 — convert straight quotes to directional typographic quotes.

  // Double quotes
  s = s.replace(/(^|[\s([{—-])"/gm, "$1\u201C");      // opening "
  s = s.replace(/"/g, "\u201D");                          // closing / remaining "

  // Single quotes — apostrophes in contractions first so they aren't
  // mis-classified as opening quotes.
  s = s.replace(/(\w)'(\w)/g, "$1\u2019$2");             // apostrophe (don't, it's)
  s = s.replace(/(^|[\s([{—-])'/gm, "$1\u2018");       // opening '
  s = s.replace(/'/g, "\u2019");                          // closing / remaining '

  return s;
}

/**
 * Recursively walks a JSON-serialisable value and applies `normalizeQuotes`
 * to every string leaf.  Returns a shallow-cloned structure (original is
 * never mutated).
 */
export function normalizeContentQuotes<T>(value: T): T {
  if (value === null || value === undefined) return value;

  if (typeof value === "string") {
    return normalizeQuotes(value) as unknown as T;
  }

  if (Array.isArray(value)) {
    return value.map(normalizeContentQuotes) as unknown as T;
  }

  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = normalizeContentQuotes(v);
    }
    return out as T;
  }

  // number, boolean, etc. — pass through
  return value;
}
